import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import NavigationFirstLayer from './NavigationFirstLayer.jsx';
import NavigationSecondLayer from './NavigationSecondLayer.jsx';
import History from '@core/scripts/components/history';
import MessageBroker from '@bonprix/pattern-library/components/message-broker';
import Topics from '@core/scripts/components/topics';
import Landmark from '@core/scripts/components/landmark-adapter';
import Messages from '@core/scripts/components/messages';
import {useCheckItemActive} from '../../hooks/use-check-item-active.js';
import {useMobileNavigationContext} from '../../context/mobile-navigation-context.jsx';
import {getSlicedLeftSubNavigationItems} from '../../../helper/sub-navigation-helper.js';
import {HeaderContext} from '@header/scripts/components/context/HeaderContext.jsx';

export default function Navigation({moveContainerToTop, moveContainerBelowHeader}) {
    const {navigationItems, texts} = useMobileNavigationContext();
    const {closeMobileNavi} = useContext(HeaderContext);

    const isItemActive = useCheckItemActive();
    const initialFirstLevelIndex = useMemo(() => navigationItems.findIndex((item) => isItemActive(item)), []);
    const slicedLeftSubNavigationItems = getSlicedLeftSubNavigationItems(navigationItems[initialFirstLevelIndex]);

    const initialSecondLevelIndex = useMemo(
        () => slicedLeftSubNavigationItems.findIndex((item) => isItemActive(item)),
        [],
    );

    const [activeIndexes, setActiveIndexes] = useState({
        firstLevel: Math.max(initialFirstLevelIndex, 0),
        secondLevel: Math.max(initialSecondLevelIndex, 0),
    });
    const [showFirstLayer, setShowFirstLayer] = useState(initialSecondLevelIndex === -1);

    const firstLayer = useRef();
    const secondLayerRef = useRef(null);
    const isInitialFirstLayer = useRef(true);
    const sendUserSeesBottomLandmark = useRef(true);

    useEffect(() => {
        if (showFirstLayer) {
            moveContainerBelowHeader();
        } else {
            moveContainerToTop();
        }

        const messages = [
            MessageBroker.subscribe(`${Topics.HISTORY}.menu`, () => {
                closeMobileNavi();
            }),
            MessageBroker.subscribe(`${Topics.HISTORY}.menuChannel`, () => {
                if (isInitialFirstLayer.current) {
                    backToFirstLayer();
                } else {
                    closeMobileNavi();
                }
            }),
        ];

        if (firstLayer.current) {
            firstLayer.current.addEventListener('scroll', (event) => {
                const {scrollHeight, scrollTop, clientHeight} = event.target;
                if (Math.abs(scrollHeight - clientHeight - scrollTop) < 1) {
                    if (sendUserSeesBottomLandmark.current) {
                        sendUserSeesBottomLandmark.current = false;
                        const lmValue = {view: `pictureNaviLayer\\bottom`};
                        Landmark.sendActionLandmark(lmValue);
                    }
                } else {
                    sendUserSeesBottomLandmark.current = true;
                }
            });
            isInitialFirstLayer.current = showFirstLayer;
            MessageBroker.publish(Messages.NAVIGATION_ACCORDION.RENDERED, {firstLayer: showFirstLayer});
        }

        return () => {
            if (firstLayer.current) {
                firstLayer.current.removeEventListener('scroll', () => {});
            }
            messages.forEach((message) => MessageBroker.unsubscribe(message));
        };
    }, []);

    useEffect(() => {
        if (showFirstLayer) {
            History.remove('menuChannel');
        } else {
            History.push('menuChannel');
        }
    }, [showFirstLayer]);

    const selectFirstLevelItem = (index) => {
        // make sure the second level index is always valid
        setActiveIndexes({
            firstLevel: index,
            secondLevel: 0,
        });
    };

    const selectSecondLevelItem = (index) => {
        setActiveIndexes((prevState) => ({
            ...prevState,
            secondLevel: index,
        }));
        setShowFirstLayer(false);
        moveContainerToTop();
    };

    const backToFirstLayer = () => {
        setShowFirstLayer(true);
        moveContainerBelowHeader();
    };

    const slide = showFirstLayer ? '' : '-slide';
    const firstLevelItem = navigationItems[activeIndexes.firstLevel];
    const slicedLeftSubNavigationItemsFirst = getSlicedLeftSubNavigationItems(firstLevelItem);

    return (
        <div className={'navigation__layers-wrapper'}>
            <div ref={firstLayer} className={'navigation__first-layer ' + slide} jsh-first-navi-layer="">
                <NavigationFirstLayer
                    activeFirstLevelIndex={activeIndexes.firstLevel}
                    setActiveFirstLevelIndex={selectFirstLevelItem}
                    selectSecondLevelItem={selectSecondLevelItem}
                    visible={showFirstLayer}
                />
            </div>
            <div className={'navigation__second-layer ' + slide} ref={secondLayerRef}>
                <NavigationSecondLayer
                    key={`${activeIndexes.firstLevel}-${activeIndexes.secondLevel}`}
                    backToFirstLayer={backToFirstLayer}
                    firstLevelTitle={firstLevelItem.title}
                    firstLevelId={firstLevelItem.id}
                    secondLevelItems={slicedLeftSubNavigationItemsFirst}
                    selectSecondLevelIndex={activeIndexes.secondLevel}
                    visible={!showFirstLayer}
                    texts={texts}
                    secondLayerRef={secondLayerRef}
                />
            </div>
        </div>
    );
}
